import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiBlob, logout } from "../lib/api";
import { useMe, useOrg } from "../lib/session";
import { ErrorText, Field, Loading, PageHeader } from "../components/ui";
import { ConfirmFields } from "./settings";
import { SignInDetails } from "../components/SignInDetails";
import { AuthenticatorCard, SessionsCard, useSecurity } from "../components/AccountSecurityCards";
export type { SecurityInfo } from "../components/AccountSecurityCards";

export function AccountSecurity({ embedded = false }: { embedded?: boolean }) {
  const me = useMe();
  const sec = useSecurity();
  const back = me.data?.organizations[0] ? `/o/${me.data.organizations[0].id}` : "/";
  return (
    <div className={embedded ? undefined : "account-page"}>
      <PageHeader title="Your account" actions={embedded ? undefined : <Link to={back}>Back to SVENEX</Link>} />
      {sec.isLoading && <Loading />}
      <ErrorText error={sec.error} />
      {sec.data && <SignInDetails email={sec.data.email} hasPassword={sec.data.has_password} />}
      {sec.data && <AuthenticatorCard info={sec.data} />}
      {sec.data && <SessionsCard info={sec.data} />}
      {sec.data && !me.data?.is_platform_admin && <DeleteAccountCard email={me.data?.user.email ?? ""} />}
    </div>
  );
}

/** DeleteAccountCard deletes the person's own account, with 30 days to change their mind. */
function DeleteAccountCard({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <section className="card danger-zone">
      <h2>Delete your account</h2>
      <p className="small">You'll be signed out everywhere and lose access to every company. For 30 days you can sign in again and keep your account. After that your name, email and sign-in details are erased for good. Customers and records you worked on stay with each company, marked "Deleted user".</p>
      <p className="muted small">If you're the only owner of a company, make someone else an owner or close the company first.</p>
      {!open ? <button className="danger" onClick={() => setOpen(true)}>Delete my account…</button> : (
        <form className="form" onSubmit={async (e) => {
          e.preventDefault();
          const d = new FormData(e.currentTarget);
          setBusy(true); setError(null);
          try {
            await api("/v1/me/deletion", { method: "POST", json: { confirm: String(d.get("confirm") ?? ""), password: String(d.get("password") ?? ""), code: String(d.get("code") ?? "") } });
            await logout();
          } catch (err) { setError(err); setBusy(false); }
        }}>
          <Field label={`Type your email to confirm: ${email}`}><input name="confirm" type="email" autoComplete="off" required maxLength={254} /></Field>
          <ConfirmFields />
          <ErrorText error={error} />
          <div className="inline">
            <button type="submit" className="danger" disabled={busy}>{busy ? "Deleting…" : "Delete my account"}</button>
            <button type="button" className="link" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}

export function MFAGate({ what }: { what: string }) {
  const me = useMe();
  const enabled = useSecurity().data?.mfa.enabled;
  return (
    <div className="center mfa-gate">
      <h2>Authenticator app needed</h2>
      <p>{what}</p>
      {enabled
        ? <p>Your app is set up. Sign out and sign back in with a code from it.</p>
        : <p>Set it up once on your phone; it takes a minute.</p>}
      <div className="inline">
        {enabled ? <button onClick={() => logout()}>Sign out</button> : <Link className="button" to="/account/security">Set up authenticator app</Link>}
        {(me.data?.organizations.length ?? 0) > 1 && <Link to="/">Switch organization</Link>}
      </div>
    </div>
  );
}

interface OrgSecurity {
  require_mfa: boolean;
  message_retention_days: number | null;
  session_idle_minutes: number | null;
  session_max_hours: number | null;
  allowed_networks: string[];
  allow_google_sign_in: boolean;
  support_access: boolean;
  your_address: string;
  members_without_mfa: string[];
  can_edit: boolean;
}

const idleChoices: [string, string][] = [["", "After 7 days (SVENEX's default)"], ["15", "After 15 minutes"], ["30", "After 30 minutes"], ["60", "After 1 hour"], ["240", "After 4 hours"], ["480", "After 8 hours"], ["1440", "After 1 day"]];
const maxChoices: [string, string][] = [["", "No limit (at most 30 days)"], ["8", "Every 8 hours"], ["12", "Every 12 hours"], ["24", "Every day"], ["168", "Every week"]];

const retentionChoices: [string, string][] = [["", "Keep conversations"], ["90", "3 months"], ["180", "6 months"], ["365", "1 year"], ["730", "2 years"], ["1825", "5 years"]];

/** OrgSecuritySettings is Settings → Security for company admins. */
export function OrgSecuritySettings() {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const me = useMe();
  const q = useQuery({ queryKey: [orgId, "org-security"], queryFn: () => api<OrgSecurity>(`${base}/security`) });
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState("");
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorText error={q.error} />;
  const v = q.data!;
  async function save(json: Record<string, unknown>, msg: string) {
    setError(null); setSaved("");
    try {
      const out = await api<OrgSecurity>(`${base}/security`, { method: "PUT", json });
      qc.setQueryData([orgId, "org-security"], out);
      qc.invalidateQueries({ queryKey: ["me"] });
      setSaved(msg);
    } catch (e) { setError(e); }
  }
  const current = v.message_retention_days == null ? "" : String(v.message_retention_days);
  return (
    <>
      <PageHeader title="Security" />
      {saved && <p className="notice small">{saved}</p>}
      <ErrorText error={error} />
      <section className="card">
        <h2>Authenticator app for everyone</h2>
        <p className="muted small">When on, everyone in this company must sign in with a code from an authenticator app, not just a password and email code. People who haven't set it up are asked to the next time they open SVENEX.</p>
        <label className="inline"><input type="checkbox" checked={v.require_mfa} disabled={!v.can_edit}
          onChange={(e) => save({ require_mfa: e.target.checked }, e.target.checked ? "Authenticator app is now required." : "Authenticator app is no longer required.")} /> Require an authenticator app</label>
        {!v.require_mfa && !me.data?.session_mfa && <p className="muted small">First <Link to={`${base}/settings/account`}>turn on your own authenticator app</Link> and sign in with it, so you can't lock yourself out.</p>}
        {v.members_without_mfa.length > 0 && <p className="small">Not set up yet: {v.members_without_mfa.join(", ")}</p>}
      </section>
      <section className="card">
        <h2>Delete old conversations</h2>
        <p className="muted small">Conversations (chat, WhatsApp and email) with no new messages for this long are deleted for good, with their AI logs. Customer records, opportunities and appointments stay. Bahrain's data protection law asks you to keep personal data only as long as you need it.</p>
        <select value={current} disabled={!v.can_edit} onChange={(e) => {
          const val = e.target.value;
          save(val ? { message_retention_days: Number(val) } : { keep_forever: true }, val ? "Old conversations will be deleted on that schedule." : "Conversations are kept.");
        }}>
          {retentionChoices.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
          {current && !retentionChoices.some(([val]) => val === current) && <option value={current}>{current} days</option>}
        </select>
      </section>
      <section className="card">
        <h2>Staying signed in</h2>
        <p className="muted small">Sign people out of a screen left unattended, and make everyone sign in again after a while. Applies to everyone in the company, on every device.</p>
        <div className="row">
          <Field label="Sign out when SVENEX isn't used">
            <select value={v.session_idle_minutes == null ? "" : String(v.session_idle_minutes)} disabled={!v.can_edit}
              onChange={(e) => save({ session_idle_minutes: Number(e.target.value || 0) }, "Saved. It applies from now on.")}>
              {withCurrent(idleChoices, v.session_idle_minutes, (n) => `After ${n} minutes`).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
          </Field>
          <Field label="Sign in again">
            <select value={v.session_max_hours == null ? "" : String(v.session_max_hours)} disabled={!v.can_edit}
              onChange={(e) => save({ session_max_hours: Number(e.target.value || 0) }, "Saved. People signed in for longer are asked to sign in again.")}>
              {withCurrent(maxChoices, v.session_max_hours, (n) => `Every ${n} hours`).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
          </Field>
        </div>
      </section>
      <NetworksCard v={v} save={save} />
      <section className="card">
        <h2>Sign-in methods</h2>
        <p className="muted small">Turn off "Continue with Google" to make everyone sign in with their email and password (and the authenticator app, when required). Useful when your company manages sign-ins itself.</p>
        <label className="inline"><input type="checkbox" checked={v.allow_google_sign_in} disabled={!v.can_edit}
          onChange={(e) => save({ allow_google_sign_in: e.target.checked }, e.target.checked ? "People can use Continue with Google again." : "Continue with Google is off for this company.")} /> Allow Continue with Google</label>
      </section>
      <section className="card">
        <h2>SVENEX support access</h2>
        <p className="muted small">When on, anyone here can give SVENEX support a one-time code (Settings → SVENEX support access) so SVENEX can use SVENEX as them for up to 4 hours: an admin's code opens the whole company, an employee's only what they can see. SVENEX also needs its own password and authenticator app. It can't see or change sign-in details, this page, the team, or customer data exports. Everything it does is in the log below, and you can end it any time.</p>
        <label className="inline"><input type="checkbox" checked={v.support_access} disabled={!v.can_edit}
          onChange={(e) => save({ support_access: e.target.checked }, e.target.checked ? "SVENEX support access is on. People can now make codes under Settings." : "SVENEX support access is off, and any access in progress has ended.")} /> Allow SVENEX support access with a code</label>
      </section>
      <SecurityLog />
    </>
  );
}

function withCurrent(choices: [string, string][], current: number | null, label: (n: number) => string): [string, string][] {
  if (current == null || choices.some(([val]) => val === String(current))) return choices;
  return [...choices, [String(current), label(current)]];
}

/** NetworksCard limits the company's data to its own networks (office, VPN). */
function NetworksCard({ v, save }: { v: OrgSecurity; save: (json: Record<string, unknown>, msg: string) => Promise<void> }) {
  // Single addresses come back as /32 (or /128); show them as typed.
  const plain = (n: string) => n.replace(/\/(32|128)$/, "");
  const [text, setText] = useState(v.allowed_networks.map(plain).join("\n"));
  const [busy, setBusy] = useState(false);
  const list = () => text.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);
  const changed = list().map(plain).join(",") !== v.allowed_networks.map(plain).join(",");
  async function apply(next: string[], msg: string) {
    setBusy(true);
    try { await save({ allowed_networks: next }, msg); } finally { setBusy(false); }
  }
  return (
    <section className="card">
      <h2>Allowed networks</h2>
      <p className="muted small">Only allow SVENEX to open this company's data from these addresses, for example your office and VPN. Leave empty to allow any network. Customers on your website, WhatsApp and booking page aren't affected.</p>
      {v.allowed_networks.length > 0 && <p className="notice small">On: only {v.allowed_networks.length === 1 ? "this address" : `these ${v.allowed_networks.length} addresses`} can open the company's data.</p>}
      <Field label="Addresses or ranges, one per line">
        <textarea rows={4} value={text} disabled={!v.can_edit} onChange={(e) => setText(e.target.value)} placeholder={"203.0.113.10\n10.20.0.0/16"} spellCheck={false} />
      </Field>
      <p className="muted small">Your address right now: <code>{v.your_address || "unknown"}</code>{v.your_address && !list().map(plain).includes(v.your_address) && v.can_edit && <> · <button type="button" className="link small" onClick={() => setText((t) => (t.trim() ? t.trim() + "\n" : "") + v.your_address)}>Add it</button></>}</p>
      {v.can_edit && (
        <div className="inline">
          <button disabled={busy || !changed} className={busy ? "is-busy" : undefined} onClick={() => apply(list(), list().length ? "Saved. Only these networks can open the company's data." : "Saved. Any network can be used.")}>Save networks</button>
          {v.allowed_networks.length > 0 && <button className="secondary" disabled={busy} onClick={() => { setText(""); apply([], "Any network can be used again."); }}>Allow any network</button>}
        </div>
      )}
    </section>
  );
}

interface LogEvent { id: string; at: string; action: string; actor: string; by_svenex: boolean; target_type: string; network?: string; metadata: Record<string, unknown> }

const logFilters: [string, string][] = [["", "Everything"], ["auth.", "Sign-ins and accounts"], ["org.", "Team and security settings"], ["privacy.", "Customer data exports and erasures"], ["admin.", "Changes by SVENEX's team"]];

const actionLabels: Record<string, string> = {
  "auth.login.succeeded": "Signed in", "auth.login.failed": "Sign-in failed (wrong password)", "auth.session.ended": "Signed out a device",
  "auth.session.ended_others": "Signed out all other devices", "auth.session.reuse_detected": "A copied sign-in was detected and stopped",
  "auth.password.changed": "Changed password", "auth.password.reset": "Reset password", "auth.email.changed": "Changed sign-in email",
  "auth.mfa.enabled": "Turned on the authenticator app", "auth.mfa.disabled": "Turned off the authenticator app",
  "auth.mfa.recovery_code_used": "Used a recovery code", "auth.mfa.recovery_codes_replaced": "Made new recovery codes",
  "auth.google.linked": "Linked a Google account", "auth.settings.unlocked": "Unlocked settings with an email code",
  "org.security.updated": "Changed security settings", "org.security.networks_cleared": "SVENEX cleared the network list (on request)",
  "org.security_log.exported": "Downloaded the security log",
  "support.code.created": "Made a SVENEX support code", "support.access.started": "SVENEX support started using SVENEX",
  "support.access.ended": "Ended SVENEX support access", "support.access.wrong_code": "SVENEX support entered a wrong code", "org.member.updated": "Changed someone's access",
  "org.invite.created": "Invited someone", "org.invite.accepted": "Joined the company",
  "privacy.contact.exported": "Downloaded a customer's data", "privacy.contact.erased": "Erased a customer", "privacy.retention.deleted": "Deleted old conversations (retention)",
  "organization.details.updated": "Changed company details", "organization.closed": "Closed the company", "organization.reopened": "Reopened the company",
  "admin.entitlements.changed": "SVENEX changed the plan's features", "admin.organization.updated": "SVENEX changed the company's account",
  "subscription.plan_changed": "Plan changed", "agent.tuned_by_operator": "SVENEX adjusted an AI agent",
  "channel.whatsapp.connected": "Connected WhatsApp", "channel.disconnected": "Disconnected a channel", "email.account.connected": "Connected a mailbox",
  "email.account.disconnected": "Disconnected a mailbox", "calendar.connected": "Connected a calendar", "calendar.disconnected": "Disconnected a calendar",
  "import.started": "Started an import", "import.undone": "Undid an import", "widget.key_rotated": "Changed a website chat key",
};

export function actionLabel(a: string) {
  return actionLabels[a] ?? a.replace(/[._]/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** SecurityLog shows what happened in the company: sign-ins, access, settings, exports. */
function SecurityLog() {
  const { base, orgId } = useOrg();
  const [filter, setFilter] = useState("");
  const [older, setOlder] = useState<LogEvent[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const q = useQuery({
    queryKey: [orgId, "security-log", filter],
    queryFn: () => api<{ events: LogEvent[] }>(`${base}/security/log?limit=15${filter ? `&action=${encodeURIComponent(filter)}` : ""}`),
    placeholderData: (prev) => prev,
  });
  const seen = new Set<string>();
  const events = [...(q.data?.events ?? []), ...older].filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)));
  async function more() {
    const last = events[events.length - 1];
    if (!last) return;
    setBusy(true); setError(null);
    try {
      const r = await api<{ events: LogEvent[] }>(`${base}/security/log?limit=25&before=${encodeURIComponent(last.at)}&before_id=${last.id}${filter ? `&action=${encodeURIComponent(filter)}` : ""}`);
      setOlder((o) => [...o, ...r.events]);
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function download() {
    setBusy(true); setError(null);
    try {
      const blob = await apiBlob(`${base}/security/log/export${filter ? `?action=${encodeURIComponent(filter)}` : ""}`);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "svenex-security-log.csv";
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  return (
    <section className="card">
      <div className="card-head">
        <h2>Security log</h2>
        <div className="inline">
          <select value={filter} onChange={(e) => { setFilter(e.target.value); setOlder([]); }} aria-label="Show">
            {logFilters.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className="secondary" disabled={busy} onClick={download}>Download CSV</button>
        </div>
      </div>
      <p className="muted small">Sign-ins, access changes, security settings and data exports, including anything SVENEX's team changed. Kept in a tamper-evident log; addresses are shown as their network only.</p>
      <ErrorText error={error ?? q.error} />
      {q.isLoading ? <Loading rows={5} /> : events.length === 0 ? <p className="empty">Nothing yet.</p> : (
        <ul className={`list security-log${q.isPlaceholderData ? " is-stale" : ""}`}>
          {events.map((ev) => (
            <li key={ev.id}>
              <span className="log-what"><b>{actionLabel(ev.action)}</b>{ev.by_svenex && <span className="tag ai-tag">SVENEX</span>}</span>
              <span className="muted small">{ev.actor}{ev.metadata?.via_svenex_support ? " · through SVENEX support" : ""}{ev.network ? ` · from ${ev.network}` : ""}</span>
              <time className="muted small" dateTime={ev.at} title={new Date(ev.at).toLocaleString()}>{new Date(ev.at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</time>
            </li>
          ))}
        </ul>
      )}
      {events.length >= 15 && <button className="show-more" disabled={busy} onClick={more}>Show older</button>}
    </section>
  );
}
